import { useCallback, useState } from "react";
import { useLatestCallback } from "./usePickerStable";
import type { PickerItemValue } from "./Picker.types";

export interface UsePickerListStateOptions {
    open?: boolean;
    defaultOpen: boolean;
    onOpenChange?: (open: boolean) => void;
    inputValue?: string;
    defaultInputValue: string;
    onInputValueChange?: (query: string) => void;
    /** Whether the popup holds a `Picker.Search`, whose text must never mirror the selection. */
    hasPopupSearch: boolean;
    /** Single-select input triggers show the chosen label; that text must not filter the list on reopen. */
    shouldBypassSelectedLabelQuery: boolean;
    selectionLabel: string | null;
}

/**
 * Open, query and tree-expansion state of a list-driven root, in controlled
 * and uncontrolled forms. The uncontrolled text is a nullable draft: `null`
 * means "nothing typed, show the selection" (an input trigger displays the
 * selected label, other roots nothing), so the label bypass is the draft
 * being `null` rather than a comparison against the label. Knows nothing
 * about items or Base UI.
 */
export const usePickerListState = <T extends PickerItemValue>({
    open,
    defaultOpen,
    onOpenChange,
    inputValue,
    defaultInputValue,
    onInputValueChange,
    hasPopupSearch,
    shouldBypassSelectedLabelQuery,
    selectionLabel,
}: UsePickerListStateOptions) => {
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const isOpenRequested = open ?? internalOpen;
    const notifyOpenChange = useLatestCallback(onOpenChange);
    const notifyInputValueChange = useLatestCallback(onInputValueChange);
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            setInternalOpen(nextOpen);
            notifyOpenChange?.(nextOpen);
        },
        [notifyOpenChange],
    );

    const [draft, setDraft] = useState<string | null>(() =>
        defaultInputValue === "" ? null : defaultInputValue,
    );
    const mirroredLabel = shouldBypassSelectedLabelQuery
        ? (selectionLabel ?? "")
        : "";
    const query = inputValue ?? draft ?? mirroredLabel;

    const handleInputValueChange = useCallback(
        (nextQuery: string, reason?: string) => {
            // Base UI's reason-less update is its "sync the outside input to
            // the selected label" path; a popup search is never that input,
            // and taking the label would filter the next open to one row.
            if (hasPopupSearch && reason === "none") {
                return;
            }
            // On an input trigger anything but a keystroke (an item press, the
            // close re-sync, a clear) is Base UI writing the selection into
            // the input: the draft goes back to "show the selection" so the
            // label never filters the next open. The text itself is not
            // compared, since on `item-press` the label in scope is still the
            // previous selection's.
            const isSelectionSync =
                shouldBypassSelectedLabelQuery && reason !== "input-change";
            setDraft(isSelectionSync ? null : nextQuery);
            notifyInputValueChange?.(nextQuery);
        },
        [
            hasPopupSearch,
            notifyInputValueChange,
            shouldBypassSelectedLabelQuery,
        ],
    );
    /** Programmatic text sync that is not a user edit, so consumers are not notified; `null` shows the selection. */
    const resetQuery = useCallback((nextQuery: string | null) => {
        setDraft(nextQuery);
    }, []);

    const [expansion, setExpansion] = useState<ReadonlyMap<T, boolean>>(
        () => new Map(),
    );
    const setExpanded = useCallback((itemValue: T, isExpanded: boolean) => {
        setExpansion((previous) =>
            new Map(previous).set(itemValue, isExpanded),
        );
    }, []);

    const isSelectedLabelQuery =
        shouldBypassSelectedLabelQuery &&
        (inputValue === undefined
            ? draft === null
            : inputValue.trim() !== "" && inputValue.trim() === selectionLabel);

    return {
        isOpenRequested,
        handleOpenChange,
        query,
        isSelectedLabelQuery,
        handleInputValueChange,
        resetQuery,
        expansion,
        setExpanded,
    };
};
